/**
 * 旧版「重算选中连续段」：完整列表顺序上的连续校验与基准范围解析。
 * 纯函数，不改航班时间；供编辑器批量栏调用。
 */

/**
 * 判断完整列表下标是否构成连续一段（中间无空洞）。
 * @param {number[]} indices - 已排序或未排序的下标
 * @returns {boolean}
 */
export function isConsecutiveIndices(indices) {
    if (!Array.isArray(indices) || indices.length === 0) {
        return false;
    }
    const sorted = [...indices].sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i += 1) {
        if (sorted[i] !== sorted[i - 1] + 1) {
            return false;
        }
    }
    return true;
}

/**
 * 在完整有序列表上定位勾选范围，并给出重算基准。
 *
 * 基准规则：
 * - 常规：选中段上一班为基准（时间不动），勾中班进入链条 → baseIndex = rangeStart - 1
 * - 从全表第一班起选：第一班为基准且自身不动 → baseIndex = 0
 * - 只勾第一班：连续但无可重算班 → calculable = false
 *
 * @param {Array<{id: string}>} orderedFlights - 按 display_order 排好的完整列表（不含筛选裁剪）
 * @param {Array<string|number>} selectedIds - 勾选的航班 id
 * @returns {{
 *   continuous: boolean,
 *   calculable?: boolean,
 *   reason?: string,
 *   message?: string,
 *   baseIndex?: number,
 *   endIndex?: number,
 *   rangeStartIndex?: number,
 *   rangeEndIndex?: number,
 *   selectedCount?: number,
 * }}
 */
export function resolveSelectedContinuousRange(orderedFlights, selectedIds) {
    if (!Array.isArray(orderedFlights) || orderedFlights.length === 0) {
        return {
            continuous: false,
            reason: "empty_list",
            message: "当前没有可重算的航班",
        };
    }

    const idSet = new Set((selectedIds || []).map((id) => String(id)));
    if (idSet.size === 0) {
        return {
            continuous: false,
            reason: "empty_selection",
            message: "请先勾选要重算的航班",
        };
    }

    const indices = [];
    orderedFlights.forEach((flight, index) => {
        if (flight?.id != null && idSet.has(String(flight.id))) {
            indices.push(index);
        }
    });

    if (indices.length === 0) {
        return {
            continuous: false,
            reason: "selection_not_found",
            message: "勾选的航班不在当前列表中，请重新勾选",
        };
    }

    if (indices.length !== idSet.size) {
        return {
            continuous: false,
            reason: "selection_not_found",
            message: "勾选的航班不在当前列表中，请重新勾选",
        };
    }

    if (!isConsecutiveIndices(indices)) {
        return {
            continuous: false,
            reason: "not_continuous",
            message: "请勾选连续的一段航班",
        };
    }

    const sorted = [...indices].sort((a, b) => a - b);
    const rangeStartIndex = sorted[0];
    const rangeEndIndex = sorted[sorted.length - 1];
    const baseIndex = rangeStartIndex === 0 ? 0 : rangeStartIndex - 1;
    const endIndex = rangeEndIndex;
    const calculable = baseIndex < endIndex;

    if (!calculable) {
        return {
            continuous: true,
            calculable: false,
            reason: "nothing_to_recalculate",
            message: "选中范围内没有可重算的航班",
            baseIndex,
            endIndex,
            rangeStartIndex,
            rangeEndIndex,
            selectedCount: sorted.length,
        };
    }

    return {
        continuous: true,
        calculable: true,
        baseIndex,
        endIndex,
        rangeStartIndex,
        rangeEndIndex,
        selectedCount: sorted.length,
    };
}

const SelectedRangeRecalcService = {
    isConsecutiveIndices,
    resolveSelectedContinuousRange,
};

export default SelectedRangeRecalcService;
